import { useState, useCallback } from 'react';
import type { MobileConfig } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync } from '../hooks/use-async';
import { TextField } from '../components/ui';

export function MobileConfigPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();

  const remote = useAsync(() => container.settings.getMobileConfig.execute(), [container]);
  const [draft, setDraft] = useState<MobileConfig | null>(null);
  const [saving, setSaving] = useState(false);

  const config: MobileConfig = draft ?? remote.data ?? {
    iosMinVersion: '1.0.0',
    androidMinVersion: '1.0.0',
    iosForceUpdate: false,
    androidForceUpdate: false,
  };

  const setField = useCallback(
    <K extends keyof MobileConfig>(key: K, value: MobileConfig[K]) => {
      setDraft((prev) => ({ ...config, ...(prev ?? {}), [key]: value }));
    },
    [config],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await container.settings.saveMobileConfig.execute({ config });
      toast.notify('Configuration mise à jour');
      setDraft(null);
      remote.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, config, toast, remote]);

  const isDirty = draft !== null;

  return (
    <div className="bbm-page">
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <h1 className="bbm-page__title">Configuration app mobile</h1>
        </div>
        <div className="bbm-toolbar__end">
          <button
            className="bbm-btn bbm-btn--primary"
            onClick={() => void save()}
            disabled={!isDirty || saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 24 }}>
        {/* iOS */}
        <div className="bbm-card" style={{ flex: 1, minWidth: 280 }}>
          <h2
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontSize: 15,
              margin: '0 0 16px',
              fontWeight: 700,
            }}
          >
            iOS
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <TextField
              label="Version minimum"
              value={config.iosMinVersion}
              onChange={(v) => setField('iosMinVersion', v)}
              placeholder="ex: 2.1.0"
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
              <input
                type="checkbox"
                checked={config.iosForceUpdate}
                onChange={(e) => setField('iosForceUpdate', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#5CA989', flexShrink: 0 }}
              />
              <span style={{ fontSize: 13.5, color: '#1E3347' }}>Mise à jour obligatoire (bloquant)</span>
            </label>
          </div>
        </div>

        {/* Android */}
        <div className="bbm-card" style={{ flex: 1, minWidth: 280 }}>
          <h2
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontSize: 15,
              margin: '0 0 16px',
              fontWeight: 700,
            }}
          >
            Android
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <TextField
              label="Version minimum"
              value={config.androidMinVersion}
              onChange={(v) => setField('androidMinVersion', v)}
              placeholder="ex: 2.1.0"
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
              <input
                type="checkbox"
                checked={config.androidForceUpdate}
                onChange={(e) => setField('androidForceUpdate', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#5CA989', flexShrink: 0 }}
              />
              <span style={{ fontSize: 13.5, color: '#1E3347' }}>Mise à jour obligatoire (bloquant)</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
